import React, { useState, useEffect } from "react";
import { Layout } from "app/images";
import "styles/auth.css";
import { FormInput } from "components/ui/FormInput";
import { Button } from "components/ui/Button";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ResetPasswordAPI } from "rest/auth";
import { ButtonLoader } from "components/Loader/ButtonLoader";
import { useNotifications } from "reapop";
import { Eye, EyeOff } from "lucide-react";

const resetPasswordSchema = yup.object({
  password: yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters"),
  password_confirmation: yup.string()
    .required("Password confirmation is required")
    .oneOf([yup.ref('password')], "Passwords must match"),
});

export default function ResetPassword() {
  const { notify } = useNotifications();
  const { token } = useParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(resetPasswordSchema) });

  const resetPasswordMutation = ResetPasswordAPI();

  useEffect(() => {
    if (!token) {
      notify("Invalid reset token", "error");
      navigate("/login");
    }
  }, [token, navigate, notify]);

  const onSubmit = (data) => {
    resetPasswordMutation.mutate(
      { ...data, token },
      {
        onSuccess: () => {
          setIsSuccess(true);
        },
      }
    );
  };

  if (isSuccess) {
    return (
      <div className="container-xxl">
        <div className="authentication-wrapper authentication-basic container-p-y">
          <div className="authentication-inner">
            <div className="card">
              <div className="card-body">
                <div className="app-brand justify-content-center">
                  <Link to="/" className="app-brand-link gap-2">
                    <span className="app-brand-logo demo">
                      <img src={Layout} alt="Logo" />
                    </span>
                  </Link>
                </div>

                <div className="text-center">
                  <div className="mb-4">
                    <div className="avatar avatar-xl mx-auto mb-3">
                      <div className="avatar-inner bg-light-success">
                        <i className="bx bx-check text-success" style={{ fontSize: "2rem" }}>
                          ✅
                        </i>
                      </div>
                    </div>
                    <h4 className="mb-2">Password Reset Successful!</h4>
                    <p className="text-muted">
                      Your password has been successfully reset. You can now login with your new password.
                    </p>
                  </div>
                  
                  <div className="d-flex justify-content-center">
                    <Link to="/login" className="btn btn-primary">
                      Go to Login
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl">
      <div className="authentication-wrapper authentication-basic container-p-y">
        <div className="authentication-inner">
          <div className="card">
            <div className="card-body">
              <div className="app-brand justify-content-center">
                <Link to="/" className="app-brand-link gap-2">
                  <span className="app-brand-logo demo">
                    <img src={Layout} alt="Logo" />
                  </span>
                </Link>
              </div>

              <h4 className="mb-2">Reset Password 🔑</h4>
              <p className="mb-4">
                Enter your new password below.
              </p>

              <form id="formAuthentication" className="mb-3" onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-3">
                  <label className="form-label" htmlFor="password">
                    New Password
                  </label>
                  <div className="input-group input-group-merge">
                    <FormInput
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter new password"
                      {...register("password")}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="input-group-text cursor_pointer"
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </span>
                  </div>
                  {errors?.password && (
                    <p className="errorMessage">{errors?.password?.message}</p>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="password_confirmation">
                    Confirm New Password
                  </label>
                  <div className="input-group input-group-merge">
                    <FormInput
                      type={showConfirmPassword ? "text" : "password"}
                      name="password_confirmation"
                      placeholder="Confirm new password"
                      {...register("password_confirmation")}
                    />
                    <span
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="input-group-text cursor_pointer"
                    >
                      {showConfirmPassword ? <EyeOff /> : <Eye />}
                    </span>
                  </div>
                  {errors?.password_confirmation && (
                    <p className="errorMessage">{errors?.password_confirmation?.message}</p>
                  )}
                </div>

                {resetPasswordMutation?.isPending ? (
                  <div className="d-flex justify-content-center">
                    <ButtonLoader />
                  </div>
                ) : (
                  <Button type="submit">Reset Password</Button>
                )}
              </form>

              <div className="text-center">
                <Link to="/login">
                  <i className="bx bx-chevron-left scaleX-n1-rtl"></i>
                  Back to login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
